/* ==========================================================================
   smellBgone PDP buy-box — variant picker, selling-plan (subscribe) toggle,
   quantity stepper, and AJAX add-to-cart. Ports the interactive parts of
   app/routes/products.$handle.tsx + ProductForm.tsx to vanilla JS.

   Reads a JSON blob ([data-product-variants]) built by the section (money is
   pre-formatted server-side so no client currency formatting is needed).
   Works per-instance so multiple product sections could coexist.
   ========================================================================== */
(function () {
  function initRoot(root) {
    if (root.__smbProductInit) return;
    root.__smbProductInit = true;

    var data = {};
    try {
      data = JSON.parse(root.querySelector('[data-product-variants]').textContent);
    } catch (e) {
      return;
    }
    var variants = data.variants || [];

    var idInput = root.querySelector('[data-variant-id]');
    var planInput = root.querySelector('[data-selling-plan]');
    var priceEl = root.querySelector('[data-price]');
    var compareEl = root.querySelector('[data-compare-price]');
    var addBtn = root.querySelector('[data-add-btn]');
    var addLabel = root.querySelector('[data-add-label]');
    // The price line above the button: the price charged, and the one-time
    // price it undercuts, struck.
    var totalEl = root.querySelector('[data-add-total]');
    var wasEl = root.querySelector('[data-add-was]');
    // A struck price the section rendered for good (a bundle's component
    // total, main-product-bundle.liquid) is not the script's to change.
    if (wasEl && wasEl.hasAttribute('data-was-fixed')) wasEl = null;
    var gallery = root.querySelector('[data-gallery-main]');
    var mainImg = gallery ? gallery.querySelector('img') : null;
    // Product-level fallback, so a variant with no image of its own falls back
    // to the featured media rather than keeping the previous variant's photo
    // (`selectedVariant?.image ?? product.images[0]`, products.$handle.tsx:695).
    // The section passes the featured media itself, because the first paint may
    // already be a variant's photo (a ?variant= link).
    var baseImgSrc = (gallery && gallery.getAttribute('data-base-src')) || (mainImg ? mainImg.getAttribute('src') : null);
    var baseImgSrcset = (gallery && gallery.getAttribute('data-base-srcset')) || (mainImg ? mainImg.getAttribute('srcset') : null);
    // The featured photo's admin focal point, to put back with it.
    var baseImgFocal = (gallery && gallery.getAttribute('data-base-focal')) || '';
    function setFocal(pos) {
      if (mainImg && mainImg.style) mainImg.style.objectPosition = pos || '';
    }
    var qtyInput = root.querySelector('[data-qty]');
    // Subscribe mode's line under the button: "Renews every month at $80.71."
    // then the section's terms. Absent when the terms setting is blank.
    var subTerms = root.querySelector('[data-sub-terms]');
    // The phone's buy bar (main-product.liquid), a mirror of the button.
    var stickyBar = root.querySelector('[data-sticky-bar]');
    var stickyPrice = root.querySelector('[data-sticky-price]');
    var stickyLabel = root.querySelector('[data-sticky-label]');
    var stickyAdd = root.querySelector('[data-sticky-add]');
    var subRenew = root.querySelector('[data-sub-terms-renew]');
    // The price line's mono "From", shown only while no size is chosen.
    var fromPrefixes = root.querySelectorAll('[data-from-prefix]');
    function showFrom(on) {
      fromPrefixes.forEach(function (p) {
        p.hidden = !on;
      });
    }

    // One-time purchase is the default; subscribing is an explicit opt-in. The
    // section renders the same state, so the page is right before this runs.
    var state = { variant: null, mode: 'once', planId: null, qty: 1 };

    function selectedOptionValues() {
      var vals = [];
      root.querySelectorAll('[data-option-index]').forEach(function (group) {
        var checked = group.querySelector('input:checked');
        if (checked) vals[parseInt(group.getAttribute('data-option-index'), 10)] = checked.value;
      });
      return vals;
    }

    function findVariant(vals) {
      // A page with no option picker at all (the bundle template — a bundle is
      // one Default Title variant, nothing to choose) has nothing to read a
      // selection from, so vals is always empty. Matching by option values
      // would then never find anything and the button would read
      // "Unavailable" forever. The one variant on the page is the answer.
      if (vals.length === 0 && variants.length === 1) return variants[0];
      return (
        variants.find(function (v) {
          return (
            v.options.length === vals.length &&
            v.options.every(function (o, i) {
              return o === vals[i];
            })
          );
        }) || null
      );
    }

    function money(v) {
      if (!v) return '';
      if (state.mode === 'sub' && v.plans && state.planId && v.plans[state.planId]) return v.plans[state.planId];
      return v.price;
    }

    // Money arrives pre-formatted ("$29.99"); comparing needs the numbers.
    function amount(m) {
      return parseFloat(String(m).replace(/[^0-9.]/g, '')) || 0;
    }

    // The price line. `reference` is the one-time price when the charge
    // undercuts it, struck only when it really is higher: a strike against an
    // identical number is noise.
    function setPrice(charging, reference) {
      if (totalEl) totalEl.textContent = charging || '';
      var struck = !!(reference && charging && amount(reference) > amount(charging));
      if (wasEl) {
        wasEl.textContent = struck ? reference : '';
        wasEl.hidden = !struck;
      }
    }

    // True until the shopper has picked a value in every visible option group.
    // The section renders nothing pressed on a product with a choice to make
    // (main-product.liquid, needs_choice); hidden single-value groups count as
    // chosen.
    function awaitingChoice() {
      var waiting = false;
      root.querySelectorAll('[data-option-index]').forEach(function (group) {
        if (!group.hidden && !group.querySelector('input:checked')) waiting = true;
      });
      return waiting;
    }

    function syncSticky() {
      if (!stickyBar || !addBtn) return;
      if (stickyLabel && addLabel) stickyLabel.textContent = addLabel.textContent;
      if (stickyPrice && totalEl) stickyPrice.textContent = totalEl.textContent;
    }

    function render() {
      var v = state.variant;
      renderPillPrices();
      // After the label and price below are written.
      setTimeout(syncSticky, 0);
      if (!v) {
        renderTerms(null);
        var choose = awaitingChoice();
        if (addBtn) {
          addBtn.disabled = true;
        }
        if (addLabel) addLabel.textContent = choose ? 'Choose a size' : 'Unavailable';
        setPrice('', null);
        if (!choose) return;
        // Nothing chosen, on first load or after the shopper clears a size:
        // back to the lifestyle photo, the "From" price and a bare URL. The
        // From price follows the purchase mode, as a chosen size's does.
        if (data.from) {
          if (state.mode === 'sub') setPrice(data.from.sub, data.from.once);
          else setPrice(data.from.once, null);
          showFrom(true);
        }
        if (mainImg && baseImgSrc) {
          mainImg.src = baseImgSrc;
          if (baseImgSrcset) mainImg.srcset = baseImgSrcset;
          else mainImg.removeAttribute('srcset');
          setFocal(baseImgFocal);
        }
        try {
          var bare = new URL(window.location.href);
          if (bare.searchParams.has('variant')) {
            bare.searchParams.delete('variant');
            window.history.replaceState({}, '', bare);
          }
        } catch (e) {}
        return;
      }
      if (idInput) idInput.value = v.id;
      if (priceEl) priceEl.textContent = money(v);
      if (compareEl) {
        compareEl.textContent = v.compare_at || '';
        compareEl.style.display = v.compare_at ? '' : 'none';
      }
      // selling plan attached only in subscribe mode
      if (planInput) planInput.value = state.mode === 'sub' && state.planId ? state.planId : '';
      // availability
      if (addBtn) addBtn.disabled = !v.available;
      if (addLabel) addLabel.textContent = !v.available ? 'Sold out' : state.mode === 'sub' ? 'Subscribe' : 'Add to cart';
      renderTerms(v);
      // The price charged; in subscribe mode the one-time price it undercuts
      // is struck beside it.
      setPrice(money(v), state.mode === 'sub' ? v.price : null);
      showFrom(false);
      // gallery — src and srcset must move together, or the browser keeps
      // painting a candidate from the stale srcset and the swap never shows.
      if (mainImg) {
        var nextSrc = v.image || baseImgSrc;
        var nextSrcset = v.image ? v.image_srcset : baseImgSrcset;
        if (nextSrc) mainImg.src = nextSrc;
        if (nextSrcset) mainImg.srcset = nextSrcset;
        else mainImg.removeAttribute('srcset');
        // Each photo's own admin focal point frames its crop, as image_tag
        // does on the server; none leaves the browser's centre.
        setFocal(v.image ? v.image_focal : baseImgFocal);
      }
      // reflect variant in URL
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url);
      } catch (e) {}
    }

    function syncSelectedLabels() {
      root.querySelectorAll('[data-option-index] label').forEach(function (l) {
        var i = l.querySelector('input');
        l.classList.toggle('is-selected', !!(i && i.checked));
      });
    }

    function update() {
      syncSelectedLabels();
      state.variant = findVariant(selectedOptionValues());
      render();
    }

    // Option selection. Pressing the chosen size again clears it (Sam,
    // 2026-09-28), back to the page's first-visit state: a radio cannot be
    // unchecked natively, so each input remembers whether it was checked before
    // this click. Space on an already-checked radio fires no click at all, so
    // the keyboard gets its own handler.
    var optionInputs = root.querySelectorAll('[data-option-index] input');
    function rememberChecked() {
      optionInputs.forEach(function (i) {
        i.__smbWasChecked = i.checked;
      });
    }
    function clearOption(input) {
      input.checked = false;
      update();
      rememberChecked();
    }
    optionInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        update();
        rememberChecked();
      });
      input.addEventListener('click', function () {
        if (!input.__smbWasChecked || input.closest('[hidden]')) return;
        clearOption(input);
        // A click leaves focus on the radio, and the chip's :focus-within
        // border is the selected colour, so a cleared size stayed lit until
        // focus moved elsewhere. Keyboard clearing (below) keeps focus: there
        // the border is the focus indicator.
        if (input.blur) input.blur();
      });
      input.addEventListener('keydown', function (e) {
        if ((e.key !== ' ' && e.key !== 'Spacebar') || !input.checked || input.closest('[hidden]')) return;
        e.preventDefault();
        clearOption(input);
      });
    });
    rememberChecked();

    // The chosen cadence as words: the select's option text up to its " · n%
    // off", without a leading "Deliver", lower-cased to sit mid-sentence
    // ("every month").
    function cadenceWords() {
      var sel = root.querySelector('[data-cadence]');
      var opt = sel && sel.options[sel.selectedIndex];
      if (!opt) return '';
      // The store's plan names read "Deliver every month"; the verb goes, so
      // the line says "Renews every month".
      var words = opt.textContent
        .split('\u00b7')[0]
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^deliver(y|ed)?\s+/i, '');
      return words.charAt(0).toLowerCase() + words.slice(1);
    }
    // Each size pill's price (pdp-size-option), in the current mode: the
    // plan price while subscribing, so the pills and the price line agree.
    function renderPillPrices() {
      root.querySelectorAll('[data-pill-price]').forEach(function (el) {
        var id = String(el.getAttribute('data-pill-price'));
        var pv = variants.find(function (x) {
          return String(x.id) === id;
        });
        if (pv) el.textContent = money(pv);
      });
    }
    function renderTerms(v) {
      if (!subTerms) return;
      var on = state.mode === 'sub' && !!v;
      subTerms.hidden = !on;
      if (on && subRenew) {
        var when = cadenceWords();
        subRenew.textContent = 'Renews ' + (when ? when + ' ' : '') + 'at ' + money(v) + '.';
      }
    }

    // Purchase mode (subscribe / once). is-selected styles the chosen box;
    // aria-pressed tells assistive tech which of the two buttons is on.
    function setMode(mode) {
      state.mode = mode;
      root.querySelectorAll('[data-purchase-opt]').forEach(function (o) {
        o.classList.toggle('is-selected', o.getAttribute('data-purchase-opt') === mode);
      });
      root.querySelectorAll('[data-purchase-mode]').forEach(function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-purchase-mode') === mode ? 'true' : 'false');
      });
      render();
    }
    root.querySelectorAll('[data-purchase-mode]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setMode(btn.getAttribute('data-purchase-mode'));
      });
    });
    var cadence = root.querySelector('[data-cadence]');
    if (cadence) {
      state.planId = cadence.value;
      // Picking a cadence IS choosing to subscribe. The select sits in the
      // Subscribe box, which is not selected by default, so without this a
      // shopper who picks "every 3 months" would silently buy once. Only a
      // change flips the mode; focusing or opening the select does not.
      cadence.addEventListener('change', function () {
        state.planId = cadence.value;
        setMode('sub');
      });
    }

    // Quantity stepper, in the One-time box (main-product.liquid). It only
    // counts in one-time mode: a subscription adds 1 (Sam, 2026-09-29), so
    // pressing + or − selects One-time. The value is a display-only <span>
    // (matches the React ProductForm); qty is read from state.qty at
    // add-to-cart time, so no form input is needed. A page with no stepper
    // (a bundle's) always adds 1.
    function setQty(n) {
      state.qty = Math.min(12, Math.max(1, n));
      if (qtyInput) qtyInput.textContent = state.qty;
      root.querySelectorAll('[data-qty-minus]').forEach(function (b) {
        b.disabled = state.qty <= 1;
      });
      root.querySelectorAll('[data-qty-plus]').forEach(function (b) {
        b.disabled = state.qty >= 12;
      });
    }
    function stepQty(by) {
      if (state.mode !== 'once') setMode('once');
      setQty(state.qty + by);
    }
    root.querySelectorAll('[data-qty-minus]').forEach(function (b) {
      b.addEventListener('click', function () {
        stepQty(-1);
      });
    });
    root.querySelectorAll('[data-qty-plus]').forEach(function (b) {
      b.addEventListener('click', function () {
        stepQty(1);
      });
    });

    // Show the buy bar on a phone while the real button is out of view.
    if (stickyBar && addBtn && 'IntersectionObserver' in window && window.matchMedia) {
      var phone = window.matchMedia('(max-width: 768px)');
      var buttonInView = true;
      var place = function () {
        stickyBar.hidden = !phone.matches || buttonInView;
        syncSticky();
      };
      new IntersectionObserver(function (entries) {
        buttonInView = entries[0].isIntersecting;
        place();
      }).observe(addBtn);
      if (phone.addEventListener) phone.addEventListener('change', place);
      stickyAdd.addEventListener('click', function () {
        if (addBtn.disabled) {
          // No size yet: take the shopper to the sizes.
          var options = root.querySelector('.smb-pdp-options-row');
          if (options) options.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        addBtn.click();
      });
    }

    // AJAX add-to-cart
    var form = root.querySelector('[data-add-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        if (!window.fetch) return; // no-JS/legacy: let the form POST normally
        e.preventDefault();
        if (!state.variant || !state.variant.available) return;
        var body = { id: state.variant.id, quantity: state.mode === 'once' ? state.qty : 1 };
        if (state.mode === 'sub' && state.planId) body.selling_plan = state.planId;
        addBtn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then(function (r) {
            // A refused add (422: not enough stock, a plan the variant lacks)
            // went nowhere, so the plain form POST below is safe and shows
            // Shopify's own reason. An add that did go through never re-posts.
            if (!r.ok) throw new Error('add refused');
            return r.json();
          })
          .then(function () {
            // Pull the drawer's real content in (it was server-rendered at page
            // load, before this add, so it still shows the old — often empty —
            // cart) then open it, rather than only patching the header badge.
            if (window.smbCartDrawer) return window.smbCartDrawer.refresh().then(window.smbCartDrawer.open);
            return fetch('/cart.js')
              .then(function (r) {
                return r.json();
              })
              .then(function (cart) {
                var badge = document.querySelector('[data-cart-count]');
                if (badge) badge.textContent = cart.item_count;
              });
          })
          .then(function () {
            if (addLabel) addLabel.textContent = 'Added';
            addBtn.classList.add('is-added');
            setTimeout(function () {
              addBtn.classList.remove('is-added');
              // render() puts back the label the current mode calls for, even
              // if the shopper switched mode in the meantime.
              render();
            }, 1400);
          })
          .catch(function () {
            form.submit();
          });
      });
    }

    // "How much do I need?" opens the How to use drawer (its coverage table)
    // and brings it into view; without the script it is an ordinary anchor.
    root.querySelectorAll('[data-open-panel]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var target = document.getElementById((link.getAttribute('href') || '').slice(1));
        if (!target) return;
        e.preventDefault();
        if (target.tagName === 'DETAILS') target.open = true;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    // Single-open accordion — opening a panel closes its siblings, matching
    // the app's Accordion (components/brand/Accordion.tsx, single-open). Native
    // <details>, so closing is instantaneous; nothing animates and there is
    // nothing for prefers-reduced-motion to strip.
    root.querySelectorAll('.smb-pdp-accordion').forEach(function (acc) {
      acc.querySelectorAll('details.smb-pdp-panel').forEach(function (panel) {
        panel.addEventListener('toggle', function () {
          if (!panel.open) return;
          acc.querySelectorAll('details.smb-pdp-panel[open]').forEach(function (other) {
            if (other !== panel) other.open = false;
          });
        });
      });
    });

    // Thumbnails
    root.querySelectorAll('[data-thumb]').forEach(function (t) {
      t.addEventListener('click', function () {
        if (mainImg) {
          mainImg.src = t.getAttribute('data-full');
          mainImg.removeAttribute('srcset');
        }
      });
    });

    // Initialise from the pre-checked options.
    update();
  }

  function scan() {
    document.querySelectorAll('[data-product-root]').forEach(initRoot);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-product-root]').forEach(initRoot);
  });
})();
